import { Injectable } from '@angular/core'
import { MsalService } from '@azure/msal-angular'
import { APP_CONFIG } from '../config/environment'

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  azureAccessToken: string | null = null
  postgrestJwtToken: string | null = null
  user: string | null = null
  userEmail: string | null = null
  private embeddedMode = false
  private embeddedUser: Record<string, unknown> | null = null
  private postgrestJwtRequest: Promise<string> | null = null

  constructor(private msalService: MsalService) {}

  enableEmbeddedMode(): void {
    this.embeddedMode = true
  }

  setToken(token: string): void {
    this.embeddedMode = true
    this.postgrestJwtToken = token
  }

  setEmbeddedUser(user: Record<string, unknown>): void {
    this.embeddedMode = true
    this.embeddedUser = user
    const sf =
      typeof user['sf'] === 'object' && user['sf'] !== null
        ? (user['sf'] as Record<string, unknown>)
        : null
    const formattedName = this.getEmbeddedFormattedName(user, sf)
    const name =
      formattedName ??
      (user['name'] as string | undefined) ??
      (user['fullName'] as string | undefined) ??
      (sf?.['fullName'] as string | undefined) ??
      (user['displayName'] as string | undefined) ??
      (user['username'] as string | undefined)
    const email =
      (user['email'] as string | undefined) ??
      (user['userEmail'] as string | undefined) ??
      (sf?.['email'] as string | undefined) ??
      (sf?.['username'] as string | undefined) ??
      (user['username'] as string | undefined)
    this.user = name ?? this.user
    this.userEmail = email ?? this.userEmail
  }

  getEmbeddedUserContext(): Record<string, unknown> | null {
    return this.embeddedUser
  }

  getEmbeddedRole(): string | undefined {
    if (!this.embeddedUser) {
      return undefined
    }

    const sf =
      typeof this.embeddedUser['sf'] === 'object' &&
      this.embeddedUser['sf'] !== null
        ? (this.embeddedUser['sf'] as Record<string, unknown>)
        : null

    return (
      (this.embeddedUser['role'] as string | undefined) ??
      (sf?.['role'] as string | undefined) ??
      (sf?.['profileName'] as string | undefined) ??
      (sf?.['userType'] as string | undefined)
    )
  }

  async getPostgrestJwtToken(signal?: AbortSignal): Promise<string> {
    if (
      this.postgrestJwtToken &&
      !this.isJwtExpired(this.postgrestJwtToken, 30)
    ) {
      return this.postgrestJwtToken
    }

    if (this.embeddedMode) {
      if (this.postgrestJwtToken) {
        throw new Error(
          'Embedded session secret expired and could not be refreshed automatically. Reload the page to sign in again.',
        )
      }
      throw new Error('Embedded authentication required')
    }

    return this.requestPostgrestJwtToken(signal)
  }

  async refreshPostgrestJwtToken(signal?: AbortSignal): Promise<string> {
    if (this.embeddedMode) {
      throw new Error(
        'Embedded session secret expired and could not be refreshed automatically. Reload the page to sign in again.',
      )
    }

    this.postgrestJwtToken = null
    return this.requestPostgrestJwtToken(signal)
  }

  async getAzureAccessToken(): Promise<string> {
    if (this.embeddedMode) {
      throw new Error('Azure access token not available in embedded mode')
    }
    if (this.azureAccessToken) {
      return this.azureAccessToken
    } else {
      const account = this.msalService.instance.getActiveAccount()

      if (!account) {
        throw new Error('No active MSAL account')
      }

      try {
        const response = await this.msalService.instance.acquireTokenSilent({
          scopes: ['api://5c6a4f59-a7c4-4bd3-83fa-03b44d81eeaa/access_as_user'],
          account,
        })

        return response.accessToken
      } catch (error) {
        console.warn('Silent token failed, attempting popup login...', error)

        const result = await this.msalService.instance.loginPopup({
          scopes: ['api://5c6a4f59-a7c4-4bd3-83fa-03b44d81eeaa/access_as_user'],
        })

        this.msalService.instance.setActiveAccount(result.account)
        return result.accessToken
      }
    }
  }

  getUser(): string | undefined {
    if (this.embeddedUser) {
      return this.user ?? (this.embeddedUser['name'] as string | undefined)
    }
    if (!this.user) {
      this.user = this.msalService.instance.getActiveAccount()?.name
    }
    return this.user
  }

  getUserEmail(): string | undefined {
    if (this.embeddedUser) {
      const sf =
        typeof this.embeddedUser['sf'] === 'object' &&
        this.embeddedUser['sf'] !== null
          ? (this.embeddedUser['sf'] as Record<string, unknown>)
          : null
      return (
        this.userEmail ??
        (this.embeddedUser['email'] as string | undefined) ??
        (this.embeddedUser['userEmail'] as string | undefined) ??
        (sf?.['email'] as string | undefined) ??
        (sf?.['username'] as string | undefined) ??
        (this.embeddedUser['username'] as string | undefined)
      )
    }
    if (!this.userEmail) {
      this,
        (this.userEmail =
          this.msalService.instance.getActiveAccount()?.username)
    }
    return this.userEmail
  }

  getAuditUser(): string | undefined {
    if (this.embeddedUser) {
      const sf =
        typeof this.embeddedUser['sf'] === 'object' &&
        this.embeddedUser['sf'] !== null
          ? (this.embeddedUser['sf'] as Record<string, unknown>)
          : null

      const formattedName = this.getEmbeddedFormattedName(this.embeddedUser, sf)
      if (formattedName) {
        return formattedName
      }

      const embeddedIdentity = [
        this.embeddedUser['name'],
        this.embeddedUser['fullName'],
        this.embeddedUser['displayName'],
        sf?.['fullName'],
        this.embeddedUser['email'],
        this.embeddedUser['userEmail'],
        this.embeddedUser['username'],
        sf?.['email'],
        sf?.['username'],
      ]
        .map((value) => (typeof value === 'string' ? value.trim() : ''))
        .find(Boolean)

      if (embeddedIdentity) {
        return embeddedIdentity
      }
    }

    // Comments and other audit entries should identify people by name. Email is
    // retained only as a fallback for accounts that do not expose a display name.
    return this.getUser() ?? this.getUserEmail()
  }

  private getEmbeddedFormattedName(
    user: Record<string, unknown>,
    sf: Record<string, unknown> | null,
  ): string | undefined {
    const firstName = this.pickFirstString([
      user['firstName'],
      user['first_name'],
      user['givenName'],
      sf?.['firstName'],
      sf?.['first_name'],
      sf?.['givenName'],
    ])

    const lastName = this.pickFirstString([
      user['lastName'],
      user['last_name'],
      user['familyName'],
      sf?.['lastName'],
      sf?.['last_name'],
      sf?.['familyName'],
    ])

    const fullName = `${firstName ?? ''} ${lastName ?? ''}`.trim()
    return fullName || undefined
  }

  private pickFirstString(values: Array<unknown>): string | undefined {
    return values
      .map((value) => (typeof value === 'string' ? value.trim() : ''))
      .find(Boolean)
  }

  private async requestPostgrestJwtToken(
    _signal?: AbortSignal,
  ): Promise<string> {
    if (!this.postgrestJwtRequest) {
      // This promise is shared by concurrent data requests. It must not inherit
      // one request's cancellation signal, otherwise a table refresh aborts the
      // token request for every caller and leaves the app loading indefinitely.
      this.postgrestJwtRequest = this.fetchPostgrestJwtToken().finally(
        () => {
          this.postgrestJwtRequest = null
        },
      )
    }

    return this.postgrestJwtRequest
  }

  private async fetchPostgrestJwtToken(signal?: AbortSignal): Promise<string> {
    const res = await fetch(APP_CONFIG.lambdaAuthUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${await this.getAzureAccessToken()}`,
      },
      body: JSON.stringify({
        lambdaName: APP_CONFIG.swaggerPostgrestJwtLambdaName,
        payload: {},
      }),
      signal,
    })

    if (!res.ok) {
      throw new Error(`Auth gateway failed: ${await res.text()}`)
    }

    const token = this.extractPostgrestJwtToken(await res.json())
    if (!token) {
      throw new Error('Auth gateway returned an invalid PostgREST token')
    }

    this.postgrestJwtToken = token
    return token
  }

  private extractPostgrestJwtToken(payload: unknown): string | null {
    let value = payload

    // The Lambda auth proxy may return the invoked Lambda's JSON body directly,
    // or wrap it once more as a JSON string. Unwrap either representation.
    for (let depth = 0; depth < 3; depth += 1) {
      if (typeof value === 'string') {
        const trimmed = value.trim()
        if (this.isJwtLike(trimmed)) {
          return trimmed
        }
        try {
          value = JSON.parse(trimmed)
          continue
        } catch {
          return null
        }
      }

      if (value && typeof value === 'object') {
        const record = value as Record<string, unknown>
        value = record['token'] ?? record['body']
        continue
      }

      return null
    }

    return null
  }

  private isJwtLike(value: string): boolean {
    return /^[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+$/.test(value)
  }

  private isJwtExpired(token: string, skewSeconds = 0): boolean {
    try {
      const payloadPart = token.split('.')[1]
      if (!payloadPart) {
        return true
      }

      const base64 = payloadPart.replace(/-/g, '+').replace(/_/g, '/')
      const padded = base64.padEnd(Math.ceil(base64.length / 4) * 4, '=')
      const decoded = atob(padded)
      const payload = JSON.parse(decoded) as { exp?: number }
      if (typeof payload.exp !== 'number') {
        return false
      }

      const now = Math.floor(Date.now() / 1000)
      return now >= payload.exp - skewSeconds
    } catch {
      return true
    }
  }
}
